/**
 * <ec-cart-drawer>
 *
 * Mini-cart drawer hydrated on demand. Triggered by the cancelable
 * `ec:cart-drawer:open` event dispatched by <ec-header> (click on
 * [data-ec-cart-trigger]). The drawer calls preventDefault() on the
 * event so <ec-header> suppresses the fallback link navigation.
 *
 * On first open the drawer fetches /cart.js, renders line items + summary
 * + shipping-progress bar. Subsequent opens reuse the cached snapshot.
 * Qty +/- and remove buttons are present in markup but inert until the
 * next commit wires them.
 */

import { trapFocus } from '@ec/focus-trap';
import { lockScroll } from '@ec/scroll-lock';
import { formatMoney } from '@theme/money-formatting';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const TRANSITION_FALLBACK_MS = 350;
const DEFAULT_MONEY_FORMAT = '{{amount}} €';
const QTY_DEBOUNCE_MS = 280;
const X_SVG =
  "<svg viewBox='0 0 24 24' width='14' height='14' fill='none' stroke='currentColor' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>";

class EcCartDrawer extends HTMLElement {
  #cart = null;
  #hydrated = false;
  #i18n = {};
  #moneyFormat = DEFAULT_MONEY_FORMAT;
  #currency = 'EUR';
  #threshold = 0;
  #cartUrl = '/cart';
  #checkoutUrl = '/checkout';

  #releaseTrap = null;
  #releaseLock = null;
  #onKeydown = null;
  #onClick = null;
  #onTriggerOpen = null;
  #onCartUpdate = null;
  #panel = null;
  #body = null;
  #foot = null;
  #countText = null;
  #previousFocus = null;
  #debounceTimers = new Map();
  #inFlight = new Set();

  // Resolved on each use instead of cached in connectedCallback: a re-render
  // replacing these nodes without replacing <ec-cart-drawer> would leave the
  // cached references pointing at detached nodes.
  get #note() {
    return this.querySelector('ec-cart-note');
  }

  get #noteWrap() {
    return this.querySelector('[data-ec-cart-note-wrap]');
  }

  connectedCallback() {
    this.#panel = this.querySelector('.ec-cart-drawer__panel');
    this.#body = this.querySelector('[data-ec-cart-body]');
    this.#foot = this.querySelector('[data-ec-cart-foot]');
    this.#countText = this.querySelector('[data-ec-cart-count-text]');

    this.#threshold = Number(this.dataset.threshold || '0');
    this.#cartUrl = this.dataset.cartUrl || '/cart';
    this.#checkoutUrl = this.dataset.checkoutUrl || '/checkout';
    this.#moneyFormat = this.dataset.moneyFormat || DEFAULT_MONEY_FORMAT;
    this.#currency = this.dataset.currency || 'EUR';

    const i18nScript = this.querySelector('[data-ec-cart-i18n]');
    if (i18nScript instanceof HTMLScriptElement) {
      try {
        this.#i18n = JSON.parse(i18nScript.textContent || '{}');
      } catch (err) {
        console.error('[ec-cart-drawer] failed to parse i18n JSON', err);
      }
    }

    this.#onKeydown = (event) => {
      if (event.key === 'Escape' && !this.hidden) {
        event.preventDefault();
        this.close();
      }
    };

    this.#onClick = async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-ec-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      // Both CTAs leave the page: the note has to land before we navigate,
      // otherwise a still-debounced comment is lost on the way to checkout.
      const navLink = target.closest('[data-ec-cart-checkout], [data-ec-cart-view-cart]');
      if (navLink instanceof HTMLAnchorElement) {
        // Modified clicks open a new tab — leave them to the browser.
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        const { href } = navLink;
        await this.#flushNote();
        window.location.href = href;
        return;
      }

      // Removal is flushed immediately — the user expects the row to vanish
      // without delay, and there's nothing to coalesce.
      const removeBtn = target.closest('[data-ec-cart-remove]');
      if (removeBtn instanceof HTMLElement) {
        event.preventDefault();
        this.#flushLineUpdate(Number(removeBtn.dataset.line), 0);
        return;
      }

      const incBtn = target.closest('[data-ec-cart-qty-inc]');
      if (incBtn instanceof HTMLElement) {
        event.preventDefault();
        const line = Number(incBtn.dataset.line);
        const current = this.#cart?.items?.[line - 1]?.quantity ?? 1;
        this.#queueLineUpdate(line, current + 1);
        return;
      }

      const decBtn = target.closest('[data-ec-cart-qty-dec]');
      if (decBtn instanceof HTMLElement) {
        event.preventDefault();
        const line = Number(decBtn.dataset.line);
        const current = this.#cart?.items?.[line - 1]?.quantity ?? 1;
        this.#queueLineUpdate(line, Math.max(0, current - 1));
      }
    };

    this.#onTriggerOpen = (event) => {
      // Tells <ec-header> we'll handle this — suppresses fallback link nav.
      event.preventDefault();
      const source =
        event.detail && event.detail.source instanceof HTMLElement ? event.detail.source : null;
      this.open({ previousFocus: source });
    };

    this.#onCartUpdate = async (event) => {
      const detail = event?.detail;
      // Ignore our own dispatches (qty/remove) — we already re-rendered.
      if (detail?.sourceId === this.id) return;

      try {
        await this.#fetchCart();
      } catch (err) {
        console.error('[ec-cart-drawer] refresh after cart:update failed', err);
        return;
      }

      if (this.hidden) {
        // Auto-open is reserved for add-to-cart triggers (PDP, quick-add,
        // sticky add). The cart page fires cart:update on every qty change
        // for badge sync — opening the drawer on top of the cart the user
        // is already viewing would be jarring.
        const source = detail?.data?.source;
        if (source === 'ec-cart-page') return;
        if (this.hasAttribute('auto-open')) this.open();
      } else {
        this.#render();
      }
    };

    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('ec:cart-drawer:open', this.#onTriggerOpen);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.#cleanup();
    for (const timer of this.#debounceTimers.values()) clearTimeout(timer);
    this.#debounceTimers.clear();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('ec:cart-drawer:open', this.#onTriggerOpen);
    if (this.#onCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    }
  }

  async open(opts = {}) {
    if (!this.hidden) return;
    this.#previousFocus =
      opts.previousFocus instanceof HTMLElement
        ? opts.previousFocus
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    this.hidden = false;
    // Double rAF so the hidden→visible transition runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });
    });

    this.#releaseLock = lockScroll();
    this.#releaseTrap = trapFocus(this);

    const closeBtn = this.querySelector('[data-ec-cart-close-button]');
    if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });

    if (!this.#hydrated) {
      this.#showSkeleton();
      try {
        await this.#fetchCart();
      } catch (err) {
        // Graceful fallback: navigate the user to the full cart page so
        // they still get feedback even when /cart.js is unreachable.
        console.error('[ec-cart-drawer] /cart.js fetch failed', err);
        this.#cleanup();
        window.location.href = this.#cartUrl;
        return;
      }
    }

    this.#render();
    this.dispatchEvent(new CustomEvent('ec:cart-drawer:opened', { bubbles: true }));
  }

  close() {
    if (this.hidden) return;
    // Flush any pending qty changes so closing mid-debounce doesn't drop
    // updates the user already triggered.
    this.#flushAllPending();
    // Same for the note — fire-and-forget, the close animation must not wait.
    this.#flushNote();
    this.classList.remove('is-open');

    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      this.hidden = true;
      this.#cleanup();
      if (this.#previousFocus && document.body.contains(this.#previousFocus)) {
        this.#previousFocus.focus({ preventScroll: true });
      }
      this.#previousFocus = null;
      this.dispatchEvent(new CustomEvent('ec:cart-drawer:closed', { bubbles: true }));
    };

    const onTransitionEnd = (event) => {
      if (event.target !== this.#panel) return;
      this.#panel.removeEventListener('transitionend', onTransitionEnd);
      finish();
    };

    if (this.#panel) {
      this.#panel.addEventListener('transitionend', onTransitionEnd);
      setTimeout(finish, TRANSITION_FALLBACK_MS);
    } else {
      finish();
    }
  }

  toggle() {
    if (this.hidden) this.open();
    else this.close();
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`cart fetch failed: ${res.status}`);
    this.#cart = await res.json();
    this.#hydrated = true;
    // /cart.js carries the note: adopt it so the drawer shows what was typed
    // on the cart page (or in another tab). The component ignores the call
    // when it is focused or still holding an unsaved value.
    const note = this.#note;
    if (note && typeof note.syncFromCart === 'function') note.syncFromCart(this.#cart.note);
  }

  // <ec-cart-note> lives in its own deferred module, so it may not be
  // upgraded yet — probe the API instead of assuming it. flush() always
  // resolves, network error included.
  #flushNote() {
    const note = this.#note;
    if (note && typeof note.flush === 'function') return note.flush();
    return Promise.resolve();
  }

  // Optimistic + debounced qty update. Updates the local cart snapshot and
  // the qty value DOM immediately so rapid clicks feel responsive, then
  // coalesces them into a single /cart/change.js POST after QTY_DEBOUNCE_MS
  // of inactivity per line. Multiple lines debounce independently.
  #queueLineUpdate(line, quantity) {
    if (!Number.isInteger(line) || line < 1) return;
    if (!this.#cart?.items?.[line - 1]) return;

    this.#cart.items[line - 1].quantity = quantity;
    const valueEl = this.querySelector(
      `.ec-cart-drawer__item[data-line='${line}'] .ec-cart-drawer__qty-value`
    );
    if (valueEl) valueEl.textContent = String(quantity);

    const existing = this.#debounceTimers.get(line);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.#debounceTimers.delete(line);
      this.#flushLineUpdate(line, quantity);
    }, QTY_DEBOUNCE_MS);
    this.#debounceTimers.set(line, timer);
  }

  async #flushLineUpdate(line, quantity) {
    if (!Number.isInteger(line) || line < 1) return;
    if (this.#inFlight.has(line)) {
      // A POST is in flight for this line — re-queue so the latest target
      // qty wins once it resolves.
      this.#queueLineUpdate(line, quantity);
      return;
    }

    this.#inFlight.add(line);
    this.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw new Error(`cart/change failed: ${res.status}`);
      const cart = await res.json();

      // The user may have clicked more on this (or another) line while this
      // POST was in flight. Capture the optimistic qty for any line whose
      // debounce timer is still pending so the response doesn't undo a
      // change that hasn't shipped yet.
      const pendingQuantities = new Map();
      for (const pendingLine of this.#debounceTimers.keys()) {
        const idx = pendingLine - 1;
        const qty = this.#cart?.items?.[idx]?.quantity;
        if (typeof qty === 'number') pendingQuantities.set(pendingLine, qty);
      }

      this.#cart = cart;

      for (const [pendingLine, qty] of pendingQuantities) {
        const idx = pendingLine - 1;
        if (this.#cart?.items?.[idx]) this.#cart.items[idx].quantity = qty;
      }

      this.#render();
      // Notify the rest of the page (header badge, etc.) that the cart
      // changed. The drawer ignores its own events via the sourceId check.
      this.dispatchEvent(
        new CartUpdateEvent(this.#cart, this.id, {
          source: 'ec-cart-drawer',
          itemCount: this.#cart.item_count,
        })
      );
    } catch (err) {
      console.error('[ec-cart-drawer] cart/change failed', err);
      // Server lost — re-sync from /cart.js so the UI doesn't lie.
      try {
        await this.#fetchCart();
        this.#render();
      } catch (refetchErr) {
        console.error('[ec-cart-drawer] re-sync after error failed', refetchErr);
      }
    } finally {
      this.#inFlight.delete(line);
      if (this.#inFlight.size === 0) this.removeAttribute('aria-busy');
    }
  }

  #flushAllPending() {
    for (const [line, timer] of this.#debounceTimers.entries()) {
      clearTimeout(timer);
      const qty = this.#cart?.items?.[line - 1]?.quantity;
      if (typeof qty === 'number') this.#flushLineUpdate(line, qty);
    }
    this.#debounceTimers.clear();
  }

  #showSkeleton() {
    if (!this.#body) return;
    this.#body.innerHTML =
      "<div class='ec-cart-drawer__skeleton' aria-hidden='true'>" +
      "<div class='ec-cart-drawer__skeleton-row'></div>".repeat(3) +
      '</div>';
    if (this.#foot) this.#foot.hidden = true;
  }

  #render() {
    if (!this.#cart || !this.#body || !this.#foot) return;

    const isEmpty = this.#cart.item_count === 0;
    // Set before the early return below — an empty cart has nothing to annotate.
    const noteWrap = this.#noteWrap;
    if (noteWrap) noteWrap.hidden = isEmpty;
    if (this.#countText) {
      this.#countText.textContent = isEmpty ? '' : `(${this.#cart.item_count})`;
    }

    if (isEmpty) {
      this.#body.innerHTML = this.#emptyTemplate();
      this.#foot.hidden = true;
      this.#foot.innerHTML = '';
      return;
    }

    const items = this.#cart.items.map((item, idx) => this.#itemTemplate(item, idx + 1)).join('');

    this.#body.innerHTML =
      this.#shippingProgressTemplate() +
      `<ul class='ec-cart-drawer__items' role='list'>${items}</ul>`;

    this.#foot.hidden = false;
    this.#foot.innerHTML = this.#summaryTemplate();
  }

  /**
   * Reads one entry of the i18n bundle emitted by the section.
   *
   * Those values arrive HTML-escaped from Liquid (`t` escapes, and the few
   * non-`t` entries carry an explicit `| escape`), so they are injected as-is:
   * running them through escapeHtml/escapeAttr again would double-escape and
   * print `&#39;` instead of an apostrophe — visible in the summary, read out
   * loud by screen readers in the aria-* labels.
   *
   * Only for the bundle. Anything coming from /cart.js is raw and still has to
   * go through escapeHtml/escapeAttr. Fallbacks must stay escape-free literals.
   */
  #t(key, fallback) {
    const value = this.#i18n[key];
    return typeof value === 'string' && value !== '' ? value : fallback;
  }

  #emptyTemplate() {
    const text = this.#t('empty', 'Votre panier est vide.');
    const ctaText = this.#t('empty_cta', 'Découvrir nos produits');
    const ctaUrl = this.#t('empty_cta_url', '/collections/all');
    return (
      `<div class='ec-cart-drawer__empty'>` +
      `<p class='ec-cart-drawer__empty-text'>${text}</p>` +
      `<a class='ec-cart-drawer__empty-cta' href='${ctaUrl}'>${ctaText}</a>` +
      `</div>`
    );
  }

  #shippingProgressTemplate() {
    if (this.#threshold <= 0) return '';
    const total = this.#cart.total_price || 0;
    const remaining = Math.max(0, this.#threshold - total);
    const percent =
      this.#threshold > 0 ? Math.min(100, Math.floor((total / this.#threshold) * 100)) : 100;
    const label =
      remaining > 0
        ? this.#t('shipping_progress', '').replace(
            /\{\{\s*amount\s*\}\}/g,
            this.#formatMoney(remaining)
          )
        : this.#t('shipping_unlocked', '');
    const ariaLabel = this.#t('shipping_progress_aria', 'Free shipping progress');
    return (
      `<div class='ec-cart-drawer__ship-progress'>` +
      `<p class='ec-cart-drawer__ship-progress-label' aria-live='polite'>${label}</p>` +
      `<div class='ec-cart-drawer__ship-progress-bar' role='progressbar' aria-valuemin='0' aria-valuemax='100' aria-valuenow='${percent}' aria-label='${ariaLabel}'>` +
      `<span class='ec-cart-drawer__ship-progress-fill' style='width:${percent}%'></span>` +
      `</div>` +
      `</div>`
    );
  }

  #itemTemplate(item, line) {
    const options = (item.options_with_values || [])
      .map(
        (opt) =>
          `<p class='ec-cart-drawer__item-option'>${escapeHtml(opt.name)} : ${escapeHtml(
            opt.value
          )}</p>`
      )
      .join('');

    const imgSrc = item.image ? `${item.image.split('?')[0]}?width=168` : '';
    const image = imgSrc
      ? `<img src='${escapeAttr(imgSrc)}' alt='${escapeAttr(item.product_title || '')}' width='84' height='99' loading='lazy'>`
      : '';

    const productUrl = escapeAttr(item.url || '#');
    const productTitle = escapeHtml(item.product_title || item.title || '');
    const removeAria = this.#t('remove_aria', 'Retirer cet article');
    const qtyIncAria = this.#t('qty_inc_aria', 'Augmenter la quantité');
    const qtyDecAria = this.#t('qty_dec_aria', 'Diminuer la quantité');
    const linePrice = this.#formatMoney(item.final_line_price);
    const original = item.original_line_price;
    const showOriginal = typeof original === 'number' && original > item.final_line_price;
    const priceMarkup = showOriginal
      ? `<span class='ec-cart-drawer__item-price-original'>${this.#formatMoney(original)}</span> ${linePrice}`
      : linePrice;

    return (
      `<li class='ec-cart-drawer__item' data-line='${line}'>` +
      `<a class='ec-cart-drawer__item-thumb' href='${productUrl}'>${image}</a>` +
      `<div class='ec-cart-drawer__item-info'>` +
      `<h3 class='ec-cart-drawer__item-name'>` +
      `<a href='${productUrl}'>${productTitle}</a>` +
      `<button type='button' class='ec-cart-drawer__item-remove' data-ec-cart-remove data-line='${line}' aria-label='${removeAria}'>${X_SVG}</button>` +
      `</h3>` +
      options +
      `<div class='ec-cart-drawer__item-bottom'>` +
      `<div class='ec-cart-drawer__qty'>` +
      `<button type='button' class='ec-cart-drawer__qty-btn' data-ec-cart-qty-dec data-line='${line}' aria-label='${qtyDecAria}'>−</button>` +
      `<span class='ec-cart-drawer__qty-value' aria-live='polite'>${item.quantity}</span>` +
      `<button type='button' class='ec-cart-drawer__qty-btn' data-ec-cart-qty-inc data-line='${line}' aria-label='${qtyIncAria}'>+</button>` +
      `</div>` +
      `<p class='ec-cart-drawer__item-price'>${priceMarkup}</p>` +
      `</div>` +
      `</div>` +
      `</li>`
    );
  }

  #summaryTemplate() {
    const subtotal = this.#cart.items_subtotal_price ?? this.#cart.total_price;
    const subtotalLabel = this.#t('subtotal', 'Sous-total');
    const shippingLabel = this.#t('shipping', 'Livraison');
    const shippingPending = this.#t('shipping_pending', 'À calculer');
    const totalLabel = this.#t('total_with_tax', 'Total TTC');
    const checkoutLabel = this.#t('checkout', 'Passer la commande');
    const viewCartLabel = this.#t('view_cart', 'Voir le panier');
    const taxNote = this.#t('tax_note', '');

    return (
      `<div class='ec-cart-drawer__summary'>` +
      `<div class='ec-cart-drawer__summary-row'><span>${subtotalLabel}</span><span aria-live='polite'>${this.#formatMoney(subtotal)}</span></div>` +
      `<div class='ec-cart-drawer__summary-row'><span>${shippingLabel}</span><span class='ec-cart-drawer__summary-pending'>${shippingPending}</span></div>` +
      `<div class='ec-cart-drawer__summary-row ec-cart-drawer__summary-row--total'><span>${totalLabel}</span><span aria-live='polite'>${this.#formatMoney(this.#cart.total_price)}</span></div>` +
      (taxNote ? `<p class='ec-cart-drawer__summary-note'>${taxNote}</p>` : '') +
      `<a class='ec-cart-drawer__checkout' href='${escapeAttr(this.#checkoutUrl)}' data-ec-cart-checkout>${checkoutLabel}</a>` +
      `<a class='ec-cart-drawer__view-cart' href='${escapeAttr(this.#cartUrl)}' data-ec-cart-view-cart>${viewCartLabel}</a>` +
      `</div>`
    );
  }

  #formatMoney(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }

  #cleanup() {
    if (typeof this.#releaseTrap === 'function') {
      this.#releaseTrap();
      this.#releaseTrap = null;
    }
    if (typeof this.#releaseLock === 'function') {
      this.#releaseLock();
      this.#releaseLock = null;
    }
  }
}

// For raw values only — /cart.js payloads, data-* attributes, anything the
// browser handed us decoded. i18n strings are pre-escaped by Liquid and go
// through #t() instead.
function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>]/g, (c) => {
    if (c === '&') return '&amp;';
    if (c === '<') return '&lt;';
    return '&gt;';
  });
}

function escapeAttr(str) {
  // Our template literals quote attributes with single quotes, so `'` must
  // be escaped too — without it a value like `Bag's` breaks out of the
  // attribute, opening the door to attribute-injection XSS via any
  // admin-controlled string (product title, line item properties, etc).
  return String(str ?? '').replace(/["'&<>]/g, (c) => {
    if (c === '"') return '&quot;';
    if (c === "'") return '&#39;';
    if (c === '&') return '&amp;';
    if (c === '<') return '&lt;';
    return '&gt;';
  });
}

if (!customElements.get('ec-cart-drawer')) {
  customElements.define('ec-cart-drawer', EcCartDrawer);
}
